import React from 'react';
import {createRefetchContainer, graphql} from 'react-relay';
import {Redirect, Route, Switch, useRouteMatch} from 'react-router-dom';
import {PeriodPage} from './Period';
import {MonthByMonthPage} from './MonthByMonth';
import {UTILIZATION_PAGES} from './UtilizationReport';
import {mapCompressedAggregatedResourceNumbers} from './UtilizationReportUtils';

const UtilizationReportTotals = ({
	theEyeOptionsNestedTree,
	startDate,
	endDate,
	filters,
	showResourceUtilization,
	setCsvDownloadFunction,
	availableFeatureFlags,
	groupBy,
	viewer: {
		company: {
			companyTarget,
			isUsingProjectAllocation,
			isUsingSchedulingPlanMode,
			aggregatedResourceNumbersByDay: compressedAggregatedResourceNumbersByDay,
			aggregatedResourceNumbersByMonth: compressedAggregatedResourceNumbersByMonth,
			resourceNumbers: totalResourceNumbers,
		},
	},
	intl,
}) => {
	const {url} = useRouteMatch();
	const aggregatedResourceNumbersByDay = mapCompressedAggregatedResourceNumbers(compressedAggregatedResourceNumbersByDay, ['availableMinutes', 'forecastBillableProjectMinutes', 'forecastTotalMinutes']);
	const aggregatedResourceNumbersByMonth = mapCompressedAggregatedResourceNumbers(compressedAggregatedResourceNumbersByMonth, ['forecastBillableUtilization', 'forecastResourceUtilization']);

	return (
		<>
			<Switch>
				<Route path={`${url}`} exact>
					<Redirect to={`${url}/${UTILIZATION_PAGES.PERIOD}`} />
				</Route>
				<Route
					path={`${url}/${UTILIZATION_PAGES.PERIOD}`}
					exact
					render={() => (
						<PeriodPage
							theEyeOptionsNestedTree={theEyeOptionsNestedTree}
							startDate={startDate}
							endDate={endDate}
							filters={filters}
							aggregatedResourceNumbersByDay={aggregatedResourceNumbersByDay}
							totalResourceNumbers={totalResourceNumbers}
							companyTarget={companyTarget}
							availableFeatureFlags={availableFeatureFlags}
							showResourceUtilization={showResourceUtilization}
							isUsingProjectAllocation={isUsingProjectAllocation}
							isUsingSchedulingPlanMode={isUsingSchedulingPlanMode}
							setCsvDownloadFunction={setCsvDownloadFunction}
							intl={intl}
							groupBy={groupBy}
						/>
					)}
				/>
				<Route
					path={`${url}/${UTILIZATION_PAGES.MONTH_BY_MONTH}`}
					exact
					render={() => (
						<MonthByMonthPage
							startDate={startDate}
							endDate={endDate}
							filters={filters}
							aggregatedResourceNumbersByDay={aggregatedResourceNumbersByDay}
							aggregatedResourceNumbersByMonth={aggregatedResourceNumbersByMonth}
							totalResourceNumbers={totalResourceNumbers}
							companyTarget={companyTarget}
							availableFeatureFlags={availableFeatureFlags}
							showResourceUtilization={showResourceUtilization}
							setCsvDownloadFunction={setCsvDownloadFunction}
							intl={intl}
							groupBy={groupBy}
						/>
					)}
				/>
			</Switch>
		</>
	);
};

const UtilizationReportTotalsQuery = graphql`
	query UtilizationReportTotals_Query($startYear: Int!, $startMonth: Int!, $startDay: Int!, $endYear: Int!, $endMonth: Int!, $endDay: Int!, $searchQuery: TaskSearchQueryType!) {
		viewer {
			actualPersonId
			component(name: "utilization_report_totals")
			...UtilizationReportTotals_viewer @arguments(searchQuery: $searchQuery, startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay)
		}
	}
`;

export {UtilizationReportTotalsQuery};

export default createRefetchContainer(
	UtilizationReportTotals,
	{
		viewer: graphql`
			fragment UtilizationReportTotals_viewer on Viewer
			@argumentDefinitions(searchQuery: {type: "TaskSearchQueryType!"}, startYear: {type: "Int!"}, startMonth: {type: "Int!"}, startDay: {type: "Int!"}, endYear: {type: "Int!"}, endMonth: {type: "Int!"}, endDay: {type: "Int!"}) {
				company {
					companyTarget
					isUsingProjectAllocation
					resourceNumbers(searchQuery: $searchQuery, startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay) {
						# Actuals
						actualBillableProjectMinutes
						actualNonBillableProjectMinutes
						actualInternalMinutes
						actualTotalMinutes
						actualOverMinutes
						actualDoneTaskMinutes
						actualBillableUtilization
						actualNonBillableUtilization
						actualInternalUtilization
						actualResourceUtilization

						# Plan
						plannedBillableProjectMinutes
						plannedNonBillableProjectMinutes
						plannedInternalMinutes
						plannedTotalMinutes
						plannedOverMinutes
						plannedDoneTaskMinutes
						plannedBillableUtilization
						plannedNonBillableUtilization
						plannedInternalUtilization
						plannedResourceUtilization

						# Actuals vs. Plan
						billableActualVsPlan
						nonBillableActualVsPlan
						internalActualVsPlan
						totalActualVsPlan

						# Remaining
						remainingBillableProjectMinutes
						remainingNonBillableProjectMinutes
						remainingInternalMinutes
						remainingTotalMinutes
						remainingOverMinutes
						remainingBillableUtilization
						remainingNonBillableUtilization
						remainingInternalUtilization
						remainingResourceUtilization

						# Forecast
						forecastBillableProjectMinutes
						forecastNonBillableProjectMinutes
						forecastInternalMinutes
						forecastTotalMinutes
						forecastOverMinutes
						forecastBillableUtilization
						forecastNonBillableUtilization
						forecastInternalUtilization
						forecastResourceUtilization

						# Availability
						workingMinutes
						timeOffMinutes
						availableMinutes

						#Performance
						performance
					}
					aggregatedResourceNumbersByDay: aggregatedResourceNumbers(
						searchQuery: $searchQuery
						startYear: $startYear
						startMonth: $startMonth
						startDay: $startDay
						endYear: $endYear
						endMonth: $endMonth
						endDay: $endDay
						aggregates: ["availableMinutes", "forecastBillableProjectMinutes", "forecastTotalMinutes"]
						aggregateLevel: DAY
					)
					aggregatedResourceNumbersByMonth: aggregatedResourceNumbers(
						searchQuery: $searchQuery
						startYear: $startYear
						startMonth: $startMonth
						startDay: $startDay
						endYear: $endYear
						endMonth: $endMonth
						endDay: $endDay
						aggregates: ["forecastBillableUtilization", "forecastResourceUtilization"]
						aggregateLevel: MONTH
					)
				}
			}
		`,
	},
	graphql`
		query UtilizationReportTotalsRefetchQuery($startYear: Int!, $startMonth: Int!, $startDay: Int!, $endYear: Int!, $endMonth: Int!, $endDay: Int!, $searchQuery: TaskSearchQueryType!) {
			viewer {
				...UtilizationReportTotals_viewer @arguments(startYear: $startYear, startMonth: $startMonth, startDay: $startDay, endYear: $endYear, endMonth: $endMonth, endDay: $endDay, searchQuery: $searchQuery)
			}
		}
	`
);
